/**
 * Structure history — every recorded version of one hierarchy (org chart or
 * product tree) in a workspace, the tree as it stood in each, and what changed
 * from the version before.
 *
 * Versions are written by the database (mig 730): a baseline, then a minor
 * version on every edit to the live tree. Spec:
 * docs/9-exploration/PRD-structure-versioning-and-transitions.md FR-1 / FR-6.
 *
 * Admins can revert to an earlier version (revert_structure_to_revision, mig
 * 746). A revert never rewrites history: it records a NEW version whose tree
 * matches the chosen one. Across an authorized reorganization it is refused —
 * a reorg is superseded, never reverted.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { History, Loader2 } from 'lucide-react';
import { useActorDisplayMap } from '../../hooks/prioritization/useActorDisplayMap';
import type { HierarchyKind } from '../../lib/hierarchy/hierarchyTypes';
import {
  diffStructureRevisions,
  formatRevisionVersion,
  orderRevisionNodesAsTree,
  structureHistoryAnchorId,
  type StructureNodeChange,
  type StructureRevision,
  type StructureRevisionNode,
} from '../../lib/hierarchy/structureRevisions';
import {
  listStructureRevisionNodes,
  listStructureRevisions,
} from '../../lib/hierarchy/structureRevisionsService';
import { useIsAdmin } from '../../hooks/prioritization/useScopedPermissions';
import { revertStructureToRevision } from '../../lib/reversals/reversalsService';
import { ReverseActionButton } from '../reversals/ReverseActionButton';

const CHANGE_LABEL: Record<StructureNodeChange, string> = {
  added: 'Added',
  renamed: 'Renamed',
  moved: 'Moved',
  renamed_and_moved: 'Renamed and moved',
  retired: 'Retired',
};

const CHANGE_CLASS: Record<StructureNodeChange, string> = {
  added: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  renamed: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  moved: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  renamed_and_moved: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  retired: 'bg-[var(--coral-dim)] text-[var(--coral)]',
};

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

interface StructureHistoryPanelProps {
  workspaceId: string | null | undefined;
  kind: HierarchyKind;
  /** Bump to reload after the live tree was edited on the host page. */
  refreshKey?: string | number;
  /** Called after a successful revert so the host can reload the live tree. */
  onReverted?: () => void;
}

export function StructureHistoryPanel({ workspaceId, kind, refreshKey = 0, onReverted }: StructureHistoryPanelProps) {
  const isAdmin = useIsAdmin();
  const [reloadTick, setReloadTick] = useState(0);
  const [revisions, setRevisions] = useState<StructureRevision[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [nodesByRevision, setNodesByRevision] = useState<Record<string, StructureRevisionNode[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    // The host page edits the live tree optimistically and persists in the
    // background; give that write a moment to land before re-reading history.
    const timer = setTimeout(() => {
      if (cancelled) return;
      setLoading(true);
      setError(null);
      listStructureRevisions(workspaceId, kind)
      .then((revs) => {
        if (cancelled) return;
        setRevisions(revs);
        setNodesByRevision({});
        // A reload means the structure just changed: show the newest version.
        setSelectedId(revs[0]?.id ?? null);
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)))
      .finally(() => !cancelled && setLoading(false));
    }, 1200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [workspaceId, kind, refreshKey, reloadTick]);

  const selected = useMemo(() => revisions.find((r) => r.id === selectedId) ?? null, [revisions, selectedId]);
  // The version this one replaced — the next-older entry in the newest-first list.
  const previous = useMemo(() => {
    if (!selected) return null;
    const i = revisions.findIndex((r) => r.id === selected.id);
    return i >= 0 ? revisions[i + 1] ?? null : null;
  }, [revisions, selected]);

  const ensureNodes = useCallback(
    async (revisionId: string) => {
      if (nodesByRevision[revisionId]) return;
      const nodes = await listStructureRevisionNodes(revisionId);
      setNodesByRevision((cur) => ({ ...cur, [revisionId]: nodes }));
    },
    [nodesByRevision],
  );

  useEffect(() => {
    const wanted = [selected?.id, previous?.id].filter((id): id is string => Boolean(id));
    Promise.all(wanted.map(ensureNodes)).catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [selected, previous, ensureNodes]);

  const actorIds = useMemo(() => revisions.map((r) => r.authorizedBy), [revisions]);
  const actorNames = useActorDisplayMap(actorIds);

  const selectedNodes = selected ? nodesByRevision[selected.id] : undefined;
  const previousNodes = previous ? nodesByRevision[previous.id] : undefined;
  const tree = useMemo(() => (selectedNodes ? orderRevisionNodesAsTree(selectedNodes) : []), [selectedNodes]);
  const diff = useMemo(
    () => (selectedNodes && previousNodes ? diffStructureRevisions(previousNodes, selectedNodes) : []),
    [selectedNodes, previousNodes],
  );
  const changeByNode = useMemo(() => new Map(diff.map((d) => [d.nodeId, d])), [diff]);
  const retired = diff.filter((d) => d.change === 'retired');

  // Revert eligibility, as far as the client can see it; the server re-checks.
  const current = revisions.find((r) => r.status === 'authorized') ?? null;
  const revertBlockedReason =
    !selected || selected.status !== 'superseded' || !current
      ? null
      : current.major !== selected.major
        ? 'A reorganization happened after this version. Plan a new reorganization instead.'
        : '';

  if (!workspaceId) return null;

  return (
    <section id={structureHistoryAnchorId(kind)} className="border border-[var(--rule)] bg-[var(--surface)] p-4" data-testid="structure-history">
      <div className="flex items-center gap-2 mb-1">
        <History className="h-4 w-4 text-[var(--text-ter)]" />
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">Structure history</h3>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--text-ter)]" />}
      </div>
      <p className="text-xs text-[var(--text-sec)] mb-3">
        Every change to this structure is kept as a version. Reports and plans record the version they were decided under, so
        they stay readable after a rename or a reorganization.
      </p>

      {error && <p className="text-xs text-[var(--coral)] mb-2">{error}</p>}

      {!loading && revisions.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)]">No versions yet. The first change to this structure records one.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-4">
          <ul className="space-y-1 max-h-80 overflow-y-auto" data-testid="structure-history-versions">
            {revisions.map((r) => {
              const isSelected = r.id === selectedId;
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className={`w-full text-left px-2.5 py-2 border ${
                      isSelected ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-transparent hover:bg-[var(--hover-tint)]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-semibold text-[var(--text-pri)]">{formatRevisionVersion(r)}</span>
                      {r.status === 'authorized' && (
                        <span className="text-[11px] px-1.5 py-0.5 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">Current</span>
                      )}
                      <span className="text-xs text-[var(--text-sec)] truncate">{r.label ?? 'Edit'}</span>
                    </div>
                    <div className="text-[11px] text-[var(--text-ter)] mt-0.5">
                      {formatWhen(r.effectiveAt)}
                      {r.authorizedBy ? ` · ${actorNames[r.authorizedBy] ?? 'Unknown'}` : ''}
                      {` · ${r.nodeCount} unit${r.nodeCount === 1 ? '' : 's'}`}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>

          <div data-testid="structure-history-tree">
            {selected && (
              <>
                <div className="text-xs text-[var(--text-sec)] mb-2">
                  <span className="font-semibold text-[var(--text-pri)]">{formatRevisionVersion(selected)}</span>
                  {previous
                    ? ` — ${diff.length === 0 ? 'no structural difference from' : `${diff.length} change${diff.length === 1 ? '' : 's'} from`} ${formatRevisionVersion(previous)}`
                    : ' — the first recorded version'}
                  {selected.rationale ? <span className="block text-[var(--text-ter)] mt-0.5">{selected.rationale}</span> : null}
                </div>
                {isAdmin && revertBlockedReason !== null && (
                  <div className="mb-2">
                    <ReverseActionButton
                      label={`Revert to ${formatRevisionVersion(selected)}`}
                      confirmTitle={`Revert the structure to ${formatRevisionVersion(selected)}`}
                      explain="The live structure is changed back to match this version, and the result is saved as a new version. Nothing in the history is removed. Units added since this version are retired, not deleted."
                      run={(reason) => revertStructureToRevision(workspaceId, kind, selected.id, reason)}
                      onDone={() => {
                        setReloadTick((t) => t + 1);
                        onReverted?.();
                      }}
                      disabled={revertBlockedReason !== ''}
                      title={revertBlockedReason || undefined}
                    />
                    {revertBlockedReason && <p className="mt-1 text-[11px] text-[var(--text-ter)]">{revertBlockedReason}</p>}
                  </div>
                )}
                {!selectedNodes ? (
                  <Loader2 className="h-4 w-4 animate-spin text-[var(--text-ter)]" />
                ) : (
                  <ul className="max-h-80 overflow-y-auto text-sm">
                    {tree.map((n) => {
                      const change = changeByNode.get(n.nodeId);
                      return (
                        <li key={n.nodeId} className="flex items-center gap-2 py-0.5" style={{ paddingLeft: `${n.depth * 16}px` }}>
                          <span className="text-[var(--text-pri)] truncate">{n.name}</span>
                          {n.level && <span className="text-[11px] text-[var(--text-ter)]">{n.level.replace(/_/g, ' ')}</span>}
                          {change && (
                            <span className={`text-[11px] px-1.5 py-0.5 ${CHANGE_CLASS[change.change]}`}>
                              {CHANGE_LABEL[change.change]}
                              {change.previousName ? ` — was "${change.previousName}"` : ''}
                              {change.change !== 'renamed' && change.change !== 'added' && change.previousPathNames
                                ? ` — was under ${change.previousPathNames.slice(0, -1).join(' › ') || 'the top level'}`
                                : ''}
                            </span>
                          )}
                        </li>
                      );
                    })}
                    {retired.map((d) => (
                      <li key={d.nodeId} className="flex items-center gap-2 py-0.5 opacity-70">
                        <span className="text-[var(--text-sec)] line-through truncate">{d.pathNames.join(' › ')}</span>
                        <span className={`text-[11px] px-1.5 py-0.5 ${CHANGE_CLASS.retired}`}>{CHANGE_LABEL.retired}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
